import React, { useState, useEffect, useRef } from 'react';
import { motion, Variants } from 'framer-motion';
import { Check, ArrowRight, ArrowLeft, Minus, MessageCircle, HelpCircle, ChevronDown, Calendar, LogOut, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// IMPORT CORRETTO DALLA TUA COSTANTE
import { LOGO_URL, WHATSAPP_URL } from "@/constants";
import { resolveStripeLookupKey } from '@/lib/planFeatures';

interface SubscriptionPlansProps {
    backLabel?: string;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ backLabel }) => {
    const navigate = useNavigate();
    const { createCheckoutSession, isAuthenticated, organization, logout, isPaid } = useAuth();
    const { t } = useTranslation();

    const isSubscriptionRoute = isAuthenticated && organization && !isPaid;

    // Logic for Back Button
    const handleBack = async () => {
        if (isSubscriptionRoute) {
            await logout();
            navigate('/login');
        } else {
            navigate(-1);
        }
    };

    const actualBackLabel = (isSubscriptionRoute) ? t('common.logout') : (backLabel || t('common.back'));

    // State
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
    const [showDemo, setShowDemo] = useState(false);
    // Piano per cui è in corso la creazione della sessione di checkout (null = nessuna).
    const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null);
    const demoRef = useRef<HTMLDivElement>(null);

    // Prices logic
    const planPrices = {
        monthly: {
            pro: 39,
            ai: 79,
            enterprise: 149
        },
        annual: {
            pro: 390,
            ai: 790,
            enterprise: 1490
        }
    };

    const getCurrentPrice = (plan: 'pro' | 'ai' | 'enterprise') => {
        return planPrices[billingCycle][plan];
    };

    // Logic to handle subscription
    const handleSubscribe = async (plan: string) => {
        if (!plan) return;
        // Ogni click crea una sessione di checkout, e ogni sessione una subscription
        // Stripe: cliccare più volte lascia dietro abbonamenti `incomplete` che poi
        // scadono. Un checkout alla volta, finché non parte il redirect.
        if (checkoutPlan) return;

        // In test Stripe alcune chiavi differiscono (es. annual_pro_2): l'override
        // vive in VITE_STRIPE_LOOKUP_OVERRIDES, la convenzione resta {ciclo}_{slug}.
        const currentPlan = resolveStripeLookupKey(billingCycle + '_' + plan);

        try {
            if (isSubscriptionRoute) {
                setCheckoutPlan(plan);
                const result = await createCheckoutSession(currentPlan);
                if (!result.success) {
                    toast.error(t('subscription.errors.checkout_error'));
                    setCheckoutPlan(null);
                    return;
                }
                toast.success(t('subscription.errors.redirect'));
                // Nessun reset dello stato: la pagina sta per lasciare il posto a Stripe
                // e i bottoni devono restare bloccati durante il redirect.
                window.location.href = result.url;
            } else {
                navigate('/');
            }
        } catch (error) {
            console.error('Errore durante la gestione della sottoscrizione:', error);
            toast.error(t('subscription.errors.generic'));
            setCheckoutPlan(null);
        }
    };

    useEffect(() => {
        if (showDemo) {
            const script = document.createElement('script');
            script.src = "https://asset-tidycal.b-cdn.net/js/embed.js";
            script.async = true;
            document.body.appendChild(script);

            return () => {
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
            };
        }
    }, [showDemo]);

    const handleBookDemo = () => {
        setShowDemo(true);
        setTimeout(() => {
            demoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const PLANS = [
        {
            id: 'pro',
            name: t('subscription.plans.pro.name'),
            price: getCurrentPrice('pro'),
            desc: t('subscription.plans.pro.desc'),
            features: t('subscription.plans.pro.features', { returnObjects: true }) as string[],
            popular: false,
            isContact: false,
            btnLabel: t('subscription.plans.pro.btn'),
            lookupKey: 'pro'
        },
        {
            id: 'ai',
            name: t('subscription.plans.ai.name'),
            price: getCurrentPrice('ai'),
            desc: t('subscription.plans.ai.desc'),
            features: t('subscription.plans.ai.features', { returnObjects: true }) as string[],
            popular: true,
            isContact: false,
            btnLabel: t('subscription.plans.ai.btn'),
            lookupKey: 'ai'
        },
        {
            // Enterprise: solo vetrina, niente checkout — il bottone apre WhatsApp
            id: 'enterprise',
            name: t('subscription.plans.enterprise.name'),
            price: getCurrentPrice('enterprise'),
            desc: t('subscription.plans.enterprise.desc'),
            features: t('subscription.plans.enterprise.features', { returnObjects: true }) as string[],
            popular: false,
            isContact: true,
            btnLabel: t('subscription.plans.enterprise.btn'),
            lookupKey: null as string | null
        }
    ];

    // Colonne: Pro · AI · Enterprise (AI è la colonna evidenziata)
    const COMPARISON_DATA = [
        {
            category: t('subscription.comparison.categories.core'),
            rows: [
                { label: t('subscription.comparison.rows.monthly_bookings'), pro: t('subscription.comparison.values.unlimited'), ai: t('subscription.comparison.values.unlimited'), enterprise: t('subscription.comparison.values.unlimited') },
                { label: t('subscription.comparison.rows.collaborators'), pro: t('subscription.comparison.values.up_to_3'), ai: t('subscription.comparison.values.up_to_10'), enterprise: t('subscription.comparison.values.unlimited_staff') },
                { label: t('subscription.comparison.rows.digital_agenda'), pro: true, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.online_booking'), pro: true, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.marketplace'), pro: true, ai: true, enterprise: true },
            ]
        },
        {
            category: t('subscription.comparison.categories.ai_goals'),
            rows: [
                { label: t('subscription.comparison.rows.ai_assistant'), pro: false, ai: t('subscription.comparison.values.ai_unlimited'), enterprise: t('subscription.comparison.values.ai_unlimited') },
                { label: t('subscription.comparison.rows.goals_plans'), pro: false, ai: true, enterprise: true },
            ]
        },
        {
            category: t('subscription.comparison.categories.staff'),
            rows: [
                { label: t('subscription.comparison.rows.staff_performance'), pro: false, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.staff_costs'), pro: false, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.monthly_reports'), pro: false, ai: false, enterprise: true },
            ]
        },
        {
            category: t('subscription.comparison.categories.marketing'),
            rows: [
                { label: t('subscription.comparison.rows.marketing_automations'), pro: false, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.google_reviews'), pro: false, ai: true, enterprise: true },
            ]
        },
        {
            category: t('subscription.comparison.categories.management'),
            rows: [
                { label: t('subscription.comparison.rows.stock_management'), pro: false, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.multi_location'), pro: false, ai: false, enterprise: true },
                { label: t('subscription.comparison.rows.custom_onboarding'), pro: false, ai: false, enterprise: true },
            ]
        },
        {
            category: t('subscription.comparison.categories.support'),
            rows: [
                { label: t('subscription.comparison.rows.support_email'), pro: true, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.support_whatsapp'), pro: false, ai: true, enterprise: true },
                { label: t('subscription.comparison.rows.support_dedicated'), pro: false, ai: false, enterprise: true },
            ]
        }
    ];

    // ... (render logic updates)

    // Animazioni strutturali rimosse per ottimizzare drasticamente il caricamento su mobile.

    const renderCell = (value: string | boolean, isPro: boolean = false) => {
        if (value === true) return (
            <div className="flex justify-center">
                <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center ${isPro ? 'bg-clip-500 text-white shadow-lg shadow-clip-500/30' : 'bg-gray-900 text-white'}`}
                >
                    <Check size={12} strokeWidth={3} />
                </div>
            </div>
        );
        if (value === false) return (
            <div className="flex justify-center">
                <Minus className="text-gray-200" size={20} strokeWidth={1.5} />
            </div>
        );
        return <span className={`text-sm font-medium ${isPro ? 'text-clip-600' : 'text-gray-900'}`}>{value}</span>;
    };

    return (
        <div className="min-h-screen bg-white overflow-y-auto font-sans selection:bg-clip-100 relative overflow-x-hidden">

            {/* Navbar Minimal */}
            <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50 px-6 h-20 flex items-center justify-between">
                <div className="flex items-center gap-2 opacity-100 transition-opacity">
                    {/* USA LA COSTANTE LOGO_URL */}
                    <img src={LOGO_URL} alt="Clip Logo" className="h-7 w-auto object-contain" />
                </div>
                <button
                    onClick={handleBack}
                    className="group flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
                >
                    {(isSubscriptionRoute) ? (
                        <LogOut size={16} className="group-hover:translate-x-1 transition-transform" />
                    ) : (
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    )}
                    {actualBackLabel}
                </button>
            </nav>

            <div className="pt-32 pb-24 px-4 sm:px-6 max-w-7xl mx-auto relative z-10">
                <div className="space-y-20">
                    {/* Hero Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center max-w-3xl mx-auto space-y-8"
                    >
                        <div className="inline-block page-intro-anim">
                            <h1 className="text-5xl md:text-6xl font-black text-gray-900 tracking-tight leading-[1.1] mb-2">
                                {t('subscription.hero.title_simple')}
                            </h1>
                            <h1 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-clip-500 to-orange-400 tracking-tight leading-[1.1]">
                                {t('subscription.hero.title_power')}
                            </h1>
                        </div>

                        <p className="text-xl text-gray-500 font-light leading-relaxed">
                            {t('subscription.hero.subtitle').split('\n').map((line, i) => (
                                <React.Fragment key={i}>
                                    {line}<br className="hidden md:block" />
                                </React.Fragment>
                            ))}
                        </p>

                        {/* Toggle */}
                        <div className="inline-flex items-center p-1.5 rounded-full border border-gray-200 bg-white shadow-sm">
                            <button
                                onClick={() => setBillingCycle('monthly')}
                                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${billingCycle === 'monthly' ? 'text-gray-900 bg-gray-100' : 'text-gray-500 hover:text-gray-900'}`}
                            >
                                {t('subscription.hero.monthly')}
                            </button>
                            <button
                                onClick={() => setBillingCycle('annual')}
                                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${billingCycle === 'annual' ? 'text-white bg-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-900'}`}
                            >
                                {t('subscription.hero.annual')} <span className="text-[10px] bg-clip-500 text-white px-1.5 py-0.5 rounded font-black tracking-wide shadow-sm">{t('subscription.hero.save_badge')}</span>
                            </button>
                        </div>
                    </motion.div>

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch perspective-1000">
                        {PLANS.map((plan, index) => {
                            return (
                                <motion.div
                                    key={plan.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="h-full"
                                >
                                    <div
                                        className={`relative p-8 rounded-3xl flex flex-col h-full transition-all duration-300 hover:-translate-y-2 hover:scale-102 hover:shadow-xl hover:z-20
                                    ${plan.popular
                                                ? 'bg-white border-2 border-clip-500 shadow-xl shadow-clip-500/20 z-10'
                                                : plan.isContact
                                                    ? 'bg-[#0F1115] text-white shadow-xl shadow-gray-900/30'
                                                    : 'bg-white border border-gray-200 hover:border-gray-300'
                                            }
                                `}
                                    >
                                        {plan.popular && (
                                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-clip-500 text-white text-xs font-bold px-3 py-1.5 rounded-2xl uppercase tracking-wider shadow-md">
                                                {t('subscription.plans.common.popular')}
                                            </div>
                                        )}

                                        <div className="mb-8">
                                            <h3 className={`text-xl font-bold ${plan.isContact ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                                            <p className={`text-sm mt-2 leading-relaxed ${plan.isContact ? 'text-gray-400' : 'text-gray-500'}`}>{plan.desc}</p>
                                        </div>

                                        <div className="mb-8">
                                            <div className="flex items-baseline gap-1">
                                                <span className={`text-5xl font-black ${plan.isContact ? 'text-white' : 'text-gray-900'}`}>
                                                    <span key={plan.price} className="inline-block transition-opacity duration-300">
                                                        €{plan.price}
                                                    </span>
                                                </span>
                                                <span className={`text-sm font-medium ${plan.isContact ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    /{billingCycle === 'annual' ? t('subscription.plans.common.year') : t('subscription.plans.common.month')}
                                                </span>
                                            </div>
                                            <div className={`text-xs font-medium mt-2 h-4 flex items-center gap-1 ${plan.isContact ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {billingCycle === 'annual' ? t('subscription.plans.common.billed_yearly') : t('subscription.plans.common.billed_monthly')}
                                                {(billingCycle === 'annual') && <span className="text-green-500 bg-green-50 px-1.5 rounded font-bold text-[10px]">{t('subscription.plans.common.save_badge')}</span>}
                                            </div>
                                        </div>

                                        <div className="space-y-4 flex-1 mb-8">
                                            {plan.features.map((feat, i) => (
                                                <div key={i} className={`flex items-start gap-3 text-sm font-medium ${plan.isContact ? 'text-gray-300' : 'text-gray-600'}`}>
                                                    <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center ${plan.isContact ? 'bg-clip-500 text-white' : 'bg-gray-100 text-gray-900'}`}>
                                                        <Check size={10} strokeWidth={4} />
                                                    </div>
                                                    {feat}
                                                </div>
                                            ))}
                                        </div>

                                        <button
                                            onClick={() => plan.isContact
                                                ? window.open(WHATSAPP_URL, '_blank', 'noopener')
                                                : handleSubscribe(plan.lookupKey!)}
                                            disabled={!plan.isContact && checkoutPlan !== null}
                                            className={`w-full py-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 group
                                disabled:opacity-60 disabled:cursor-not-allowed
                                ${plan.popular
                                                    ? 'bg-clip-500 text-white hover:bg-clip-600 shadow-lg shadow-clip-500/30 hover:shadow-clip-500/50'
                                                    : plan.isContact
                                                        ? 'bg-white text-gray-900 hover:bg-gray-100'
                                                        : 'bg-gray-900 text-white hover:bg-black hover:shadow-xl'
                                                }
                            `}>
                                            {plan.btnLabel}
                                            {plan.isContact
                                                ? <MessageCircle size={16} className="text-[#25D366]" />
                                                : checkoutPlan === plan.lookupKey
                                                    ? <Loader2 size={16} className="animate-spin" />
                                                    : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Clean Comparison Table */}
                    <div className="pt-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">{t('subscription.comparison.title')}</h2>

                        <div className="relative border-t border-gray-200 lg:overflow-visible overflow-x-auto pb-6">
                            <div className="min-w-[800px] md:min-w-0">
                                {/* Header */}
                                <div className="md:sticky top-20 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200">
                                    <div className="grid grid-cols-4 max-w-6xl mx-auto">
                                        <div className="p-4 md:p-6 text-xs font-bold text-gray-400 uppercase tracking-widest flex items-end pb-4">
                                            {t('subscription.comparison.features_header')}
                                        </div>
                                        {PLANS.map((plan) => (
                                            <div key={plan.id} className={`p-4 text-center flex flex-col items-center justify-end pb-4 ${plan.popular ? 'bg-clip-50/10' : ''}`}>
                                                <h3 className={`font-bold text-base ${plan.popular ? 'text-clip-600' : 'text-gray-900'}`}>{plan.name}</h3>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="max-w-6xl mx-auto">
                                    {/* Colonna evidenziata: AI (terza di quattro) */}
                                    <div className="absolute top-0 bottom-0 left-[50%] w-[25%] -translate-x-0 bg-clip-50/20 pointer-events-none z-0"></div>

                                    {COMPARISON_DATA.map((section, sIdx) => (
                                        <div key={sIdx} className="relative z-10">
                                            <div className="grid grid-cols-4 bg-gray-50/50 border-b border-gray-100">
                                                <div className="col-span-4 p-3 pl-6 text-xs font-bold uppercase tracking-wider text-gray-500">
                                                    {section.category}
                                                </div>
                                            </div>
                                            {section.rows.map((row: any, rIdx) => (
                                                <motion.div
                                                    key={rIdx}
                                                    initial={{ opacity: 0 }}
                                                    whileInView={{ opacity: 1 }}
                                                    viewport={{ once: true, margin: "-20px" }}
                                                    className="grid grid-cols-4 border-b border-gray-100 hover:bg-gray-50/50 transition-colors group"
                                                >
                                                    <div className="col-span-1 p-4 md:pl-6 flex items-center">
                                                        <span className="text-sm font-medium text-gray-700 flex items-center gap-2 group-hover:text-gray-900 transition-colors">
                                                            {row.label}
                                                            <HelpCircle size={14} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-help" />
                                                        </span>
                                                    </div>

                                                    <div className="flex p-4 items-center justify-center">
                                                        {renderCell(row.pro)}
                                                    </div>

                                                    <div className="p-4 flex items-center justify-center border-x border-gray-100/50 relative">
                                                        {renderCell(row.ai, true)}
                                                    </div>

                                                    <div className="flex p-4 items-center justify-center">
                                                        {renderCell(row.enterprise)}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Clean Footer with Pulse Effect */}
                    <div className="pt-16 pb-8 border-t border-gray-200 text-center">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('subscription.footer.doubts')}</h3>
                        <p className="text-gray-500 mb-8 max-w-md mx-auto">{t('subscription.footer.subtitle')}</p>

                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <button
                                onClick={handleBookDemo}
                                className="px-8 py-3.5 bg-gray-100 text-gray-900 rounded-xl font-bold hover:bg-gray-200 transition-all text-sm flex items-center justify-center gap-2"
                            >
                                <Calendar size={18} />
                                {t('subscription.footer.book_demo')}
                            </button>
                            <button
                                onClick={() => window.location.href = 'mailto:info@clipbooking.com'}
                                className="px-8 py-3.5 bg-gray-100 text-gray-900 rounded-xl font-bold hover:bg-gray-200 transition-all text-sm flex items-center justify-center gap-2"
                            >
                                <Mail size={18} />
                                {t('subscription.footer.email_contact')}
                            </button>
                            <button
                                onClick={() => window.open('https://wa.me/447593288804', '_blank')}
                                className="px-8 py-3.5 bg-gray-900 text-white rounded-xl font-bold transition-all shadow-lg hover:bg-black hover:scale-105 active:scale-95 flex items-center justify-center gap-2 text-sm"
                            >
                                <MessageCircle size={18} className="text-[#25D366]" />
                                {t('subscription.footer.whatsapp')}
                            </button>
                        </div>
                    </div>

                    {/* TidyCal Embed Section */}
                    {showDemo && (
                        <div className="overflow-hidden" ref={demoRef}>
                            <div className="border-t border-gray-200 py-16 bg-gray-50/50">
                                <div className="max-w-4xl mx-auto text-center mb-8">
                                    <h3 className="text-3xl font-black text-gray-900 mb-2">{t('subscription.demo.title')}</h3>
                                    <p className="text-gray-500">{t('subscription.demo.subtitle')}</p>
                                </div>
                                <div className="flex justify-center px-4">
                                    <div className="tidycal-embed w-full max-w-[800px] shadow-2xl rounded-2xl overflow-hidden bg-white" data-path="coblostudio/prenota-una-consulenza-gratuita-di-clip-business"></div>
                                </div>
                                <div className="text-center mt-8">
                                    <button
                                        onClick={() => setShowDemo(false)}
                                        className="text-gray-400 hover:text-gray-600 text-sm font-bold flex items-center justify-center gap-1 mx-auto"
                                    >
                                        <ChevronDown size={14} className="rotate-180" /> {t('subscription.demo.close')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default SubscriptionPlans;